import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon, ClockIcon, FlagIcon } from 'lucide-react';
import { BoardMotif, TwoChairs } from './BoardMotif';
import { INSIDE_PAGES, bandBg } from '../data/assets';

/**
 * "Peek inside" — 1) una Experiment Card REAL completa (EC-66, Vol. 4), con el
 * mismo formato de 10 campos de las 129 cartas; 2) un carrusel con páginas
 * reales de los índices aprobados. Si se carga una captura en
 * data/assets.ts, se muestra la imagen; si no, una miniatura diseñada.
 */
export function LookInside() {
  const trackRef = useRef<HTMLUListElement>(null);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(true);
  const updateArrows = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    setCanPrev(el.scrollLeft > 4);
    setCanNext(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, []);
  useEffect(() => {
    updateArrows();
    window.addEventListener('resize', updateArrows);
    return () => window.removeEventListener('resize', updateArrows);
  }, [updateArrows]);
  const scrollByPage = (dir: 1 | -1) => {
    const el = trackRef.current;
    if (!el) return;
    const item = el.querySelector('li');
    const step = item ? item.getBoundingClientRect().width + 16 : 260;
    const visible = Math.max(1, Math.floor(el.clientWidth / step));
    el.scrollBy({
      left: dir * step * visible,
      behavior: 'smooth'
    });
  };
  return <section aria-labelledby="peek-heading" className="w-full bg-sand" data-id="element-139" data-mp="3nf83dzhjckmi">
      <div className="mx-auto max-w-6xl px-5 pb-16 pt-16 sm:pb-20 sm:pt-20" data-id="element-140" data-mp="3ikauqto64da5">
        <div className="text-center" data-id="element-141" data-mp="12rmt8zyg2vb">
          <p className="text-[11px] font-bold uppercase tracking-[1.6px] text-terra-deep" data-id="element-142" data-mp="13synfosote3">
            A peek inside
          </p>
          <h2 id="peek-heading" className="chalk-underline mt-3 font-display text-[28px] text-sage sm:text-5xl" data-id="element-143" data-mp="34qjhpbb8ab9o">
            Open any card. Offer it in your next session.
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-base text-ink-soft" data-id="element-144" data-mp="13nshbyfbp0wn">
            Every one of the 129 cards follows the same ten fields, on one
            page. Here is a real one from Volume 4, word for word.
          </p>
        </div>

        <ExperimentCard data-id="element-145" data-mp="1dgrdq2h29vqt" />

        <h3 className="mt-14 text-center font-display text-2xl text-sage sm:text-3xl" data-id="element-146" data-mp="1hg091brr21oh">
          More pages from the volumes
        </h3>

        <div className="relative mt-6" data-id="element-147" data-mp="kraotesngwzc">
          <div className="pointer-events-none absolute inset-x-0 bottom-5 top-0 z-10 flex items-center justify-between sm:-inset-x-3" data-id="element-148" data-mp="ksbzvj9o1jh2">
            <ArrowButton direction="prev" disabled={!canPrev} onClick={() => scrollByPage(-1)} data-id="element-149" data-mp="tqe92m3jziff" />
            <ArrowButton direction="next" disabled={!canNext} onClick={() => scrollByPage(1)} data-id="element-150" data-mp="yno0266lc25d" />
          </div>
          <ul ref={trackRef} onScroll={updateArrows} className="-mx-5 flex snap-x snap-mandatory gap-4 overflow-x-auto scroll-smooth px-5 pb-5 [scrollbar-width:thin]" data-id="element-151" data-mp="2a3maaddi8u86">
            {INSIDE_PAGES.map(page => <li key={page.title} className="shrink-0 basis-[270px] snap-start sm:basis-[300px]" data-id="element-152" data-mp="2ait0dxjm54r1">
                <figure className="overflow-hidden rounded-2xl border border-sand-dark bg-cream shadow-card" data-id="element-153" data-mp="26gxyaqtuync7">
                  <div className="relative aspect-[16/10] w-full overflow-hidden" data-id="element-154" data-mp="21jo2mtfvlffx">
                    {page.image ? <img src={page.image} alt={`${page.volume}: ${page.title}`} loading="lazy" className="absolute inset-0 h-full w-full object-cover object-top" data-id="element-155" data-mp="1wlmf9wbf2tyn" /> : <div className="absolute inset-0 flex flex-col" data-id="element-156" data-mp="3hwmjbcnjxcad">
                        <div className={`h-1.5 w-full ${bandBg[page.band]}`} data-id="element-157" data-mp="3hui787fi16z0" />
                        <div className="relative flex flex-1 flex-col p-4" data-id="element-158" data-mp="3p0cc8x217emq">
                          <span className="text-[9px] font-bold uppercase tracking-[1.4px] text-ink-muted" data-id="element-159" data-mp="3txun4szm03xd">
                            {page.volume}
                          </span>
                          <span className="mt-2 max-w-[78%] font-display text-[17px] leading-[1.15] text-sage" data-id="element-160" data-mp="3k06q1wej6kc1">
                            {page.title}
                          </span>
                          <BoardMotif className="absolute bottom-3 right-3 h-14 w-14 text-teal/40" data-id="element-161" data-mp="3azrs9xkdrcrn" />
                        </div>
                      </div>}
                  </div>
                  <figcaption className="flex items-center gap-2 border-t border-sand-dark px-3.5 py-2.5 text-[12px] font-bold uppercase tracking-wide text-ink-soft" data-id="element-162" data-mp="319ysruxiio3r">
                    <span className={`h-2 w-2 rounded-full ${bandBg[page.band]}`} data-id="element-163" data-mp="2ncjyocwbds5g" />
                    {page.type}
                  </figcaption>
                </figure>
              </li>)}
          </ul>
          <p className="chalk-note mt-1 text-center text-base text-ink-soft sm:text-lg" data-id="element-164" data-mp="28pl7mj1dczr9">
            129 Experiment Cards · 29 worksheets · 6 field cards across the six
            volumes
          </p>
        </div>
      </div>
    </section>;
}
const runSteps = ['“Pick something light: your commute, your inbox.”', '“Imagine it in that chair. How does it look?”', '“Say one sentence to it, starting with ‘you’.”', '“What was it like to say it there, not to me?”'];
function Field({
  label,
  children
}: {
  label: string;
  children: React.ReactNode;
}) {
  return <div className="border-t border-sand-dark py-3" data-id="element-165" data-mp="31bmga0kdejfq">
      <dt className="text-[10px] font-bold uppercase tracking-[1.4px] text-sage" data-id="element-166" data-mp="2wct6ai4alakb">
        {label}
      </dt>
      <dd className="mt-1 text-[14px] leading-snug text-ink" data-id="element-167" data-mp="3acc7ss2llnnn">{children}</dd>
    </div>;
}
function GradeDial({
  level
}: {
  level: 0 | 1 | 2;
}) {
  const labels = ['Gentle', 'Moderate', 'Intense'];
  return <span className="inline-flex items-center gap-1.5" data-id="element-168" data-mp="231o30hfvirjo">
      {labels.map((l, i) => <span key={l} className={`h-2 w-5 rounded-full ${i <= level ? 'bg-ochre' : 'bg-sand-dark'}`} data-id="element-169" data-mp="27ywgw5tzo9f4" data-mpi={i} />)}
      <span className="ml-1 text-[12px] font-bold text-ochre-deep" data-id="element-170" data-mp="2bzzwcr84k3ft">
        {labels[level]}
      </span>
    </span>;
}
function ExperimentCard() {
  return <article aria-label="Experiment Card EC-66, The Low-Stakes First Chair" className="mx-auto mt-9 max-w-2xl overflow-hidden rounded-2xl border border-sand-dark bg-cream shadow-card" data-id="element-171" data-mp="2lq1buumewdj4">
      <div className="h-1.5 w-full bg-sage" data-id="element-172" data-mp="2vqbs6o3qu12i" />
      <div className="p-5 sm:p-7" data-id="element-173" data-mp="2vnfrwt4fb0aw">
        <div className="flex items-start justify-between gap-4" data-id="element-174" data-mp="2zrdp2ya6u11b">
          <div data-id="element-175" data-mp="2zoj6lcaqiqaj">
            <span className="text-[10px] font-bold uppercase tracking-[1.4px] text-ink-muted" data-id="element-176" data-mp="11biv0kn7p28g">
              Vol. 4 · Block A · Setting up chair work
            </span>
            <h3 className="mt-1.5 font-display text-[24px] leading-tight text-sage sm:text-3xl" data-id="element-177" data-mp="15exddhymhup2">
              EC-66 The Low-Stakes First Chair
            </h3>
            <p className="mt-1 text-sm text-ink-soft" data-id="element-178" data-mp="t1k6dw2qr0bl">
              Introduce chair work to a hesitant client
            </p>
          </div>
          <TwoChairs className="hidden w-28 flex-none text-sage sm:block" data-id="element-179" data-mp="o1flzuypplg3" />
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2" data-id="element-180" data-mp="k3rnkhxt9ajg">
          <GradeDial level={0} data-id="element-181" data-mp="1gqwgcy5w7cey" />
          <span className="inline-flex items-center gap-1.5 text-[12px] font-bold text-ink-soft" data-id="element-182" data-mp="1gptoamwu33xg">
            <ClockIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-183" data-mp="1bra0si2ldk2z" />5 min
          </span>
          <span className="text-[12px] font-bold text-ink-soft" data-id="element-184" data-mp="17tm2r65lrb6v">Family: Dialogue</span>
        </div>

        <dl className="mt-4" data-id="element-185" data-mp="12uiwxjhst0xu">
          <Field label="When to offer it" data-id="element-186" data-mp="3shgvi1jasytw">
            Curious but wary client; “I’d feel silly talking to a chair.”
          </Field>
          <Field label="Setup" data-id="element-187" data-mp="3nhugpnntnaiz">
            One empty chair opposite, slightly angled; you to the side.
          </Field>
          <div className="my-1 rounded-xl border-l-4 border-terra bg-terra/[0.07] px-4 py-3" data-id="element-188" data-mp="1re7k2lw2n5ow">
            <dt className="text-[10px] font-bold uppercase tracking-[1.4px] text-terra-deep" data-id="element-189" data-mp="1vinmueae1wzw">
              Offer line
            </dt>
            <dd className="mt-1 font-display text-[17px] leading-snug text-ink" data-id="element-190" data-mp="1mgeiy79ml1vh">
              “Would you try speaking to a chair about something small, just to
              see what it’s like? Or we can skip it.”{' '}
              <span className="font-sans text-[12px] font-bold text-terra-deep" data-id="element-191" data-mp="2f5lh35563pg7">
                [Pause. Wait for a yes or a no.]
              </span>
            </dd>
          </div>
          <Field label="Run it" data-id="element-192" data-mp="25f0iviteomxt">
            <ol className="grid gap-1" data-id="element-193" data-mp="25e8o2a7mut8l">
              {runSteps.map((s, i) => <li key={s} className="flex gap-2" data-id="element-194" data-mp="2t4l2m3xdvo85" data-mpi={i}>
                  <span className="font-bold text-sage" data-id="element-195" data-mp="2t2rvkniuz1zm">{i + 1}.</span>
                  {s}
                </li>)}
            </ol>
          </Field>
          <div className="grid border-t border-sand-dark sm:grid-cols-2 sm:gap-6" data-id="element-196" data-mp="mfki3eaty4oy">
            <div className="py-3" data-id="element-197" data-mp="hiave6ets74t">
              <dt className="text-[10px] font-bold uppercase tracking-[1.4px] text-sage" data-id="element-198" data-mp="hgzohxq32j9h">
                Grade down
              </dt>
              <dd className="mt-1 text-[14px] leading-snug text-ink" data-id="element-199" data-mp="r7astldoiugw">
                Describe the thing in the chair only; no speaking to it.
              </dd>
            </div>
            <div className="border-t border-sand-dark py-3 sm:border-t-0" data-id="element-200" data-mp="3juexnr5v29h">
              <dt className="text-[10px] font-bold uppercase tracking-[1.4px] text-sage" data-id="element-201" data-mp="3upm008q2zopf">
                Grade up
              </dt>
              <dd className="mt-1 text-[14px] leading-snug text-ink" data-id="element-202" data-mp="cg2196q7uz14">
                Add a second sentence: “What do you want from it?”
              </dd>
            </div>
          </div>
          <Field label="Watch for" data-id="element-203" data-mp="1nzo6yim4ynla">
            Nervous laughter; the voice dropping into a real tone.
          </Field>
          <Field label="Common misstep → fix" data-id="element-204" data-mp="373tjg7ya07xe">
            Switching to a charged topic “since we’re here” → keep it light.
          </Field>
          <Field label="Assimilate" data-id="element-205" data-mp="3261kitz7rljp">
            “What surprised you?” “What would make the chair easier next
            time?”
          </Field>
          <div className="mt-1 flex gap-2.5 rounded-xl bg-sand px-4 py-3" data-id="element-206" data-mp="2tcer3aihaudo">
            <FlagIcon className="mt-0.5 h-4 w-4 flex-none text-terra-deep" aria-hidden="true" data-id="element-207" data-mp="2tc6fdly3dv42" />
            <div data-id="element-208" data-mp="2oen1wo97xmd6">
              <dt className="text-[10px] font-bold uppercase tracking-[1.4px] text-terra-deep" data-id="element-209" data-mp="2oaz6tko4krwc">
                Safety flag
              </dt>
              <dd className="mt-1 text-[14px] leading-snug text-ink" data-id="element-210" data-mp="2eee2l1jxjktk">
                Not during crisis or flooding. If a light figure brings strong
                feeling, return to talking with you and keep chair work for
                later.
              </dd>
            </div>
          </div>
        </dl>
      </div>
    </article>;
}
function ArrowButton({
  direction,
  disabled,
  onClick
}: {
  direction: 'prev' | 'next';
  disabled: boolean;
  onClick: () => void;
}) {
  const Icon = direction === 'prev' ? ChevronLeftIcon : ChevronRightIcon;
  return <button type="button" onClick={onClick} disabled={disabled} aria-label={direction === 'prev' ? 'Previous pages' : 'Next pages'} className="pointer-events-auto flex h-11 w-11 items-center justify-center rounded-full border border-sand-dark bg-white text-sage shadow-card transition-[opacity,transform,background-color] duration-150 ease-out hover:bg-cream active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-terra disabled:pointer-events-none disabled:opacity-0" data-id="element-211" data-mp="209m0ws2cpxcp">
      <Icon className="h-5 w-5" aria-hidden="true" data-id="element-212" data-mp="1llsmhap050vp" />
    </button>;
}